/**
 * MapaVendedorPreview.jsx — Versão 1
 * Layout idêntico ao MapaCompradorPreview.jsx
 */
import { useRef, useState, Fragment } from 'react'
import { Printer } from 'lucide-react'
import api from '../../api/client'
import AlterarComissaoModal from '../../components/modals/AlterarComissaoModal'
import AdicionarDespesaModal from '../../components/modals/AdicionarDespesaModal'
import IsentarDefesaModal from '../../components/modals/IsentarDefesaModal'
import AplicarIsencaoModal from '../../components/modals/AplicarIsencaoModal'
import { openPdf, downloadPdf } from '../../utils/pdfUtils'
import { criasTxt, criasTotais } from './criasUtils'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')

// ─── Formatadores ─────────────────────────────────────────────────────────────
const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const num = v => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0)
const fmtDate = s => {
  if (!s) return ''
  try {
    const dateStr = String(s).split('T')[0]
    const [y, m, d] = dateStr.split('-')
    if (y && m && d) return `${d}/${m}/${y}`
    return new Date(s).toLocaleDateString('pt-BR')
  } catch { return s }
}

// ─── Estilos inline (idênticos ao MapaCompradorPreview) ──────────────────────
const S = {
  page: {
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontSize: 13,
    color: '#000',
    background: '#fff',
    padding: '16px 24px 20px',
    borderBottom: '2px solid #e5e7eb',
    marginBottom: 0,
  },
  hdr: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    paddingBottom: 6, marginBottom: 6,
  },
  hdrLogo: { maxHeight: 64, maxWidth: 160, objectFit: 'contain' },
  hdrTitle: { flex: 1, padding: '0 20px', textAlign: 'center' },
  hdrT1: { fontSize: 16, fontWeight: 700 },
  hdrT2: { fontSize: 12, marginTop: 3 },
  hdrBank: { textAlign: 'right', fontSize: 11, lineHeight: 1.6 },
  compBlock: { marginBottom: 14 },
  compLbl: { fontSize: 11, fontWeight: 700, marginBottom: 3 },
  compVal: { fontSize: 12, lineHeight: 1.3 },
  tbl: { width: '100%', borderCollapse: 'collapse', marginBottom: 4, fontSize: 12 },
  th: { background: '#d0d0d0', border: '1px solid #999', padding: '2px 6px', textAlign: 'center', fontWeight: 700, fontSize: 11 },
  td: { border: '1px solid #ccc', padding: '5px 6px', verticalAlign: 'top' },
  tdR: { border: '1px solid #ccc', padding: '5px 6px', textAlign: 'right', verticalAlign: 'top' },
  tdC: { border: '1px solid #ccc', padding: '5px 6px', textAlign: 'center', verticalAlign: 'top' },
  desc: { fontSize: 10, color: '#555', marginTop: 2 },
  totais: { textAlign: 'right', fontSize: 12, lineHeight: 1.8, marginBottom: 14 },
  thPar: { background: '#e8e8e8', border: '1px solid #999', padding: '2px 6px', textAlign: 'center', fontWeight: 700, fontSize: 11 },
  dbVend: { border: '1px solid #ccc', padding: '8px 12px', fontSize: 12, marginTop: 10 },
  footer: { textAlign: 'center', fontSize: 10, color: '#999', marginTop: 12, borderTop: '1px solid #eee', paddingTop: 6 },
}

const DEFAULT_COLUNAS_V1_VEND = ['lote', 'comprador', 'qtd', 'comissao', 'despesas', 'valor_unitario', 'total', 'liquido']

// ─── Uma página de vendedor ───────────────────────────────────────────────────
function MapaPage({ vend, leilao, organizador, showDbVendedor, showDbOrganizador = true, colunasAtivas = DEFAULT_COLUNAS_V1_VEND }) {
  const showCurral      = colunasAtivas.includes('curral')
  const showComprador   = colunasAtivas.includes('comprador')
  const showQtd         = colunasAtivas.includes('qtd')
  const showComissao    = colunasAtivas.includes('comissao')
  const showDespesas    = colunasAtivas.includes('despesas')
  const showValorUnit   = colunasAtivas.includes('valor_unitario')
  const showTotal       = colunasAtivas.includes('total')
  const showLiquido     = colunasAtivas.includes('liquido')
  const lotes = vend.lotes_venda || []
  const lotesDefesa = vend.lotes_defesa || []
  const lotesNaoMov = vend.lotes_nao_movimentados || []
  const criasTot = criasTotais([...lotes, ...lotesDefesa, ...lotesNaoMov])

  let totalVv = 0, totalCv = 0, totalDesp = 0, totalLiq = 0, totalDesconto = 0, qtdAnimais = 0

  lotes.forEach(l => {
    totalVv += l.vv || 0
    totalCv += l.cv_val || 0
    totalDesp += l.desp || 0
    totalDesconto += l.desconto || 0
    totalLiq += l.liquido || 0
    qtdAnimais += l.qtd || 0
  })

  // Totais defesa
  let totalDefVv = 0, totalDefCv = 0, qtdDefAnimais = 0
  lotesDefesa.forEach(l => {
    totalDefVv += l.vv || 0
    totalDefCv += l.cv_val || 0
    totalDesp  += l.desp || 0
    qtdDefAnimais += l.qtd || 0
  })

  // GTA + Nota em defesa (se habilitado)
  let gtaNotaDef = 0
  if (lotesDefesa.length > 0) {
    const fluxoLeilao = leilao.fluxo_comissao || ''
    const cobrarDefesa = leilao.gta_cobrar_defesa || false
    if (fluxoLeilao === 'gta_nota' && cobrarDefesa) {
      const valorPorAnimal = parseFloat(leilao.valor_por_animal) || 0
      const valorNota = parseFloat(leilao.valor_nota) || 0
      const gtaModo = leilao.gta_modo_cobranca || 'por_vendedor'
      if (valorPorAnimal > 0 || valorNota > 0) {
        const gtaTotal = valorPorAnimal * qtdDefAnimais
        const notaTotal = gtaModo === 'por_lote' ? valorNota * lotesDefesa.length : valorNota * 1
        gtaNotaDef = gtaTotal + notaTotal
      }
    }
  }

  // Totais não movimentados
  let qtdNaoMovAnimais = 0
  lotesNaoMov.forEach(l => {
    qtdNaoMovAnimais += l.qtd || 0
  })

  // Total geral de animais (vendas + defesas + não movimentados)
  const qtdAnimaisGeral = qtdAnimais + qtdDefAnimais + qtdNaoMovAnimais

  const parcelas = []
  lotes.forEach(l => {
    ;(l.parcelas || []).forEach(p => {
      parcelas.push({
        comprador: l.comprador_nome || '',
        data: p.data,
        prazo: p.prazo,
        liquido: p.liquido,
      })
    })
  })

  // Agrupa parcelas do mesmo comprador com mesma data de vencimento
  const normalizeDate = (d) => {
    if (!d) return ''
    const s = String(d)
    if (s.includes('T')) return s.split('T')[0]
    return s.slice(0, 10)
  }
  const groupedMap = {}
  parcelas.forEach(p => {
    const key = `${p.comprador}|${normalizeDate(p.data)}`
    if (groupedMap[key]) {
      groupedMap[key].liquido += p.liquido
    } else {
      groupedMap[key] = { ...p, data: normalizeDate(p.data) }
    }
  })
  const parcelasAgrupadas = Object.values(groupedMap).sort((a, b) => (a.data || '').localeCompare(b.data || ''))

  const orgDb = showDbOrganizador ? organizador.dados_bancarios : null
  const logoSrc = organizador.logo_url
    ? (organizador.logo_url.startsWith('http')
        ? organizador.logo_url
        : `${API_BASE}${organizador.logo_url.startsWith('/') ? '' : '/'}${organizador.logo_url}`)
    : null

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.hdr}>
        <div style={{ flex: '0 0 160px' }}>
          {logoSrc
            ? <img src={logoSrc} alt="logo" style={S.hdrLogo} />
            : <span style={{ fontWeight: 700, fontSize: 15 }}>{organizador.nome}</span>}
        </div>
        <div style={{ ...S.hdrTitle, flex: 1 }}>
          <div style={S.hdrT1}>MAPA DO VENDEDOR</div>
          <div style={S.hdrT2}>{organizador.nome}</div>
          <div style={S.hdrT2}>{leilao.nome} — {fmtDate(leilao.data_leilao)}</div>
        </div>
        <div style={{ flex: '0 0 160px', textAlign: 'right' }}>
          {leilao.banner_url ? (
            <img
              src={leilao.banner_url.startsWith('http') ? leilao.banner_url : `${API_BASE}${leilao.banner_url.startsWith('/') ? '' : '/'}${leilao.banner_url}`}
              alt="banner leilão"
              style={{ maxHeight: 64, maxWidth: 160, objectFit: 'contain', marginLeft: 'auto' }}
            />
          ) : showDbVendedor && vend.dados_bancarios ? (
            <div style={S.hdrBank}>
              {vend.dados_bancarios.banco && <div>BANCO: {vend.dados_bancarios.banco}</div>}
              {vend.dados_bancarios.agencia && <div>AG: {vend.dados_bancarios.agencia} C/C: {vend.dados_bancarios.conta}</div>}
              {vend.dados_bancarios.pix && <div>PIX: {vend.dados_bancarios.pix}</div>}
            </div>
          ) : (
            <div style={S.hdrBank}>
              {organizador.telefone && <div>Tel: {organizador.telefone}</div>}
              {organizador.endereco && <div>{organizador.endereco}</div>}
              {(organizador.cidade || organizador.uf) && <div>{[organizador.cidade, organizador.uf].filter(Boolean).join(' - ')}</div>}
            </div>
          )}
        </div>
      </div>

      {/* Nome do vendedor centralizado */}
      <div style={{ border: '1px solid #999', padding: '1.5px 12px', textAlign: 'center', marginBottom: 6, background: '#f0f0f0' }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>{vend.nome.toUpperCase()}</div>
      </div>

      {/* Dados vendedor + fazenda */}
      <div style={{ display: 'flex', gap: 24, marginBottom: 6, fontSize: 12, lineHeight: 1.6 }}>
        <div style={{ flex: 1 }}>
          <div><strong>CPF/CNPJ:</strong> {vend.cpf_cnpj || '–'}</div>
          <div><strong>Cidade:</strong> {vend.cidade ? `${vend.cidade.toUpperCase()}${vend.uf ? ` - ${vend.uf.toUpperCase()}` : ''}` : '–'}</div>
          <div><strong>Tel:</strong> {vend.telefone || '–'}</div>
        </div>
        <div style={{ flex: 1 }}>
          <div><strong>Fazenda:</strong> {vend.fazenda ? vend.fazenda.toUpperCase() : '–'}</div>
          <div><strong>IE:</strong> {vend.fazenda_ie || '–'}</div>
          <div><strong>Cidade:</strong> {vend.fazenda_cidade ? `${vend.fazenda_cidade.toUpperCase()}${vend.fazenda_uf ? ` - ${vend.fazenda_uf.toUpperCase()}` : ''}` : '–'}</div>
        </div>
      </div>

      {/* Tabela lotes */}
      <table style={S.tbl}>
        <thead>
          <tr>
            <th style={{ ...S.th, width: '5%' }}>Lote</th>
            {showCurral      && <th style={{ ...S.th, width: '7%' }}>{organizador.label_curral || 'Curral'}</th>}
            {showComprador   && <th style={{ ...S.th }}>Comprador</th>}
            {showQtd         && <th style={{ ...S.th, width: '5%' }}>Qtd</th>}
            {showComissao    && <th style={{ ...S.th, width: '10%' }}>Comissão<br />de Venda</th>}
            {showDespesas    && <th style={{ ...S.th, width: '10%' }}>Despesas</th>}
            {showValorUnit   && <th style={{ ...S.th, width: '11%' }}>Valor<br />Unitário</th>}
            {showTotal       && <th style={{ ...S.th, width: '10%' }}>Total</th>}
            {showLiquido     && <th style={{ ...S.th, width: '12%' }}>Líquido</th>}
          </tr>
        </thead>
        <tbody>
          {lotes.map((l, i) => {
            const desc = [l.tipo_animal, l.raca, l.sexo, l.idade].filter(Boolean).join(' ').toUpperCase()
            return (
              <Fragment key={i}>
              <tr style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={S.tdC}><strong>{l.numero_lote}</strong></td>
                {showCurral && <td style={S.tdC}>{l.curral || '–'}</td>}
                {showComprador && (
                  <td style={S.td}>
                    <div style={{ fontWeight: 700 }}>{l.comprador_nome.toUpperCase()}</div>
                    {desc && <div style={{ ...S.desc, fontWeight: 700 }}>{desc}</div>}
                  </td>
                )}
                {showQtd       && <td style={S.tdC}>{l.qtd}</td>}
                {showComissao  && <td style={S.tdR}>{brl(l.cv_val)}</td>}
                {showDespesas  && <td style={S.tdR}>{brl(l.desp || 0)}</td>}
                {showValorUnit && <td style={S.tdR}>{brl(l.vv_unit)}</td>}
                {showTotal     && <td style={S.tdR}>{num(l.vv)}</td>}
                {showLiquido   && <td style={S.tdR}>{brl(l.liquido)}</td>}
              </tr>
              {criasTxt(l) && (
                <tr style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td colSpan={1 + [showCurral, showComprador, showQtd, showComissao, showDespesas, showValorUnit, showTotal, showLiquido].filter(Boolean).length} style={{ ...S.td, fontSize: 10, padding: '1px 6px' }}>
                    <strong>Crias:</strong> {criasTxt(l)}
                  </td>
                </tr>
              )}
              </Fragment>
            )
          })}
        </tbody>
      </table>

      {/* Totalizadores — três colunas: vendas | médias | comissões */}
      {(() => {
        const fluxoLeilao = leilao.fluxo_comissao || ''
        let totalGtaNotaVend = 0
        if (fluxoLeilao === 'gta_nota') {
          const valorPorAnimal = parseFloat(leilao.valor_por_animal) || 0
          const valorNota = parseFloat(leilao.valor_nota) || 0
          const gtaModo = leilao.gta_modo_cobranca || 'por_vendedor'
          if (valorPorAnimal > 0 || valorNota > 0) {
            const gtaTotal = valorPorAnimal * qtdAnimais
            const notaTotal = gtaModo === 'por_lote' ? valorNota * lotes.length : valorNota * 1
            totalGtaNotaVend = gtaTotal + notaTotal
          }
        }

        const porSexo = {}
        lotes.forEach(l => {
          const sexo = (l.sexo || 'Misto').toUpperCase()
          const label = sexo.includes('FEM') || sexo.includes('FÊMEA') ? 'Fêmea'
            : sexo.includes('MACH') || sexo.includes('MACHO') ? 'Macho' : 'Misto'
          if (!porSexo[label]) porSexo[label] = { total: 0, cv: 0, qtd: 0 }
          porSexo[label].total += (l.vv || 0)
          porSexo[label].cv += (l.cv_val || 0)
          porSexo[label].qtd += (l.qtd || 0)
        })
        const totalAnimaisSexo = Object.values(porSexo).reduce((s, v) => s + v.qtd, 0)
        const mediaGeral = qtdAnimais > 0 ? (totalVv - totalCv - totalDesconto) / qtdAnimais : 0

        return (
          <div style={{ display: 'flex', gap: 0, marginBottom: 6, fontSize: 12 }}>
            {/* Esquerda: resumo de lotes */}
            <div style={{ flex: 1, padding: '4px 14px', lineHeight: 1.7, textAlign: 'center' }}>
              <div><strong>Vendas:</strong> Lotes: {lotes.length}, Qtde: {qtdAnimais}, Valor: {brl(totalVv)}</div>
              {lotesDefesa.length > 0 && <div><strong>Defesas:</strong> Lotes: {lotesDefesa.length}, Qtde: {qtdDefAnimais}, Valor: {brl(totalDefVv)}</div>}
              <div><strong>Quantidade animais:</strong> {qtdAnimaisGeral}</div>
              {criasTot.txt && <div><strong>Crias:</strong> {criasTot.txt}</div>}
            </div>
            {/* Centro: médias */}
            <div style={{ flex: 1, padding: '4px 14px', lineHeight: 1.7, textAlign: 'center' }}>
              {['Fêmea', 'Macho', 'Misto'].map(s => {
                const data = porSexo[s]
                if (!data || data.qtd === 0) return null
                const media = (data.total - data.cv) / data.qtd
                return <div key={s}><strong>Média {s}:</strong> {brl(media)}</div>
              }).filter(Boolean)}
              <div style={{ fontWeight: 700, marginTop: 2 }}><strong>Média Geral:</strong> {brl(mediaGeral)}</div>
            </div>
            {/* Direita: comissões */}
            <div style={{ flex: 1, padding: '4px 14px', lineHeight: 1.7, textAlign: 'center' }}>
              <div><strong>Comissão venda:</strong> {brl(totalCv)}</div>
              <div><strong>Despesas:</strong> {brl(totalDesp)}</div>
              {totalDesconto > 0 && <div style={{ color: '#92400e' }}><strong>Descontos:</strong> -{brl(totalDesconto)}</div>}
            </div>
          </div>
        )
      })()}

      {/* Seção Lotes Não Movimentados / Defesa */}
      {(lotesDefesa.length > 0 || lotesNaoMov.length > 0) && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6, color: '#e65100' }}>
            Lotes Não Movimentados / Defesa
          </div>
          <table style={S.tbl}>
            <thead>
              <tr>
                <th style={{ ...S.th, width: '6%' }}>Lote</th>
                <th style={{ ...S.th, width: '6%' }}>Sit</th>
                <th style={{ ...S.th, width: '6%' }}>Qtd</th>
                <th style={{ ...S.th }}>Descrição</th>
                <th style={{ ...S.th, width: '14%' }}>Valor</th>
                <th style={{ ...S.th, width: '14%' }}>Comissão</th>
              </tr>
            </thead>
            <tbody>
              {[...lotesDefesa.map(l => ({ ...l, _sit: 'DEF' })), ...lotesNaoMov.map(l => ({ ...l, _sit: 'N/M' }))].map((l, i) => {
                const desc = [l.tipo_animal, l.raca, l.sexo, l.idade].filter(Boolean).join(' ').toUpperCase()
                return (
                  <Fragment key={i}>
                  <tr style={{ background: '#fff3e0' }}>
                    <td style={S.tdC}><strong>{l.numero_lote}</strong></td>
                    <td style={{ ...S.tdC, color: '#e65100', fontWeight: 700 }}>{l._sit}</td>
                    <td style={S.tdC}>{l.qtd}</td>
                    <td style={S.td}>{desc || '—'}</td>
                    <td style={S.tdR}>{l.vv ? brl(l.vv) : '—'}</td>
                    <td style={S.tdR}>{l.cv_val ? brl(l.cv_val) : '—'}</td>
                  </tr>
                  {criasTxt(l) && (
                    <tr style={{ background: '#fff3e0' }}>
                      <td colSpan={6} style={{ ...S.td, fontSize: 10, padding: '1px 6px' }}>
                        <strong>Crias:</strong> {criasTxt(l)}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
          {lotesDefesa.length > 0 && (
            <div style={{ textAlign: 'right', fontSize: 12, lineHeight: 1.6, marginTop: 6 }}>
              <div><strong>Defesas</strong> Lotes: {lotesDefesa.length}  Qtde: {qtdDefAnimais}</div>
              <div>Total defesa: {brl(totalDefVv)}</div>
              <div>Comissão de defesa: {brl(totalDefCv)}</div>
              {gtaNotaDef > 0 && <div>GTA + Nota: {brl(gtaNotaDef)}</div>}
              <div style={{ fontWeight: 700, marginTop: 2 }}>Total comissão defesa: {brl(totalDefCv + gtaNotaDef)}</div>
            </div>
          )}
        </div>
      )}

      {/* Grand total consolidado quando há vendas + defesas */}
      {lotes.length > 0 && lotesDefesa.length > 0 && (
        <div style={{ textAlign: 'right', fontSize: 15, fontWeight: 700, borderTop: '2px solid #1f2937', paddingTop: 8, marginBottom: 10, color: '#1a3c6b' }}>
          Total Comissões + Despesas (venda + defesa): {brl(totalCv + totalDefCv + gtaNotaDef + totalDesp)}
        </div>
      )}

      {/* Tabela parcelas */}
      {parcelasAgrupadas.length > 0 && (
        <>
          <table style={S.tbl}>
            <thead>
              <tr>
                <th style={{ ...S.thPar, width: '5%' }}>#</th>
                <th style={{ ...S.thPar, width: '30%' }}>Comprador</th>
                <th style={{ ...S.thPar, width: '15%' }}>Vencimento</th>
                <th style={{ ...S.thPar, width: '18%' }}>Prazo</th>
                <th style={{ ...S.thPar, width: '20%' }}>Líquido</th>
              </tr>
            </thead>
            <tbody>
              {parcelasAgrupadas.map((p, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={S.tdC}>{i + 1}</td>
                  <td style={S.td}>
                    <div style={{ fontWeight: 700 }}>{p.comprador.toUpperCase()}</div>
                  </td>
                  <td style={S.tdC}>{fmtDate(p.data)}</td>
                  <td style={S.tdC}>{p.prazo}</td>
                  <td style={S.tdR}>{brl(p.liquido)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* TOTAL GERAL */}
          <div style={{ border: '1px solid #999', marginTop: 6, marginBottom: 8 }}>
            <div style={{ background: '#f0f0f0', borderBottom: '1px solid #999', padding: '2px 0', textAlign: 'center', fontWeight: 700, fontSize: 13 }}>
              TOTAL GERAL
            </div>
            <div style={{ display: 'flex' }}>
              <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
                <div style={{ fontSize: 10, color: '#555', marginBottom: 2 }}>Total Animais</div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{qtdAnimaisGeral}</div>
                {criasTot.txt && <div style={{ fontSize: 10, color: '#555' }}>Crias: {criasTot.txt}</div>}
              </div>
              <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
                <div style={{ fontSize: 10, color: '#555', marginBottom: 2 }}>Total Vendido</div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{brl(totalVv)}</div>
              </div>
              <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
                <div style={{ fontSize: 10, color: '#555', marginBottom: 2 }}>Total Comissões</div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{brl(totalCv + totalDefCv + gtaNotaDef + totalDesp)}</div>
              </div>
              <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: '#555', marginBottom: 2 }}>Total Líquido</div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{brl(totalLiq)}</div>
              </div>
            </div>
          </div>

          {/* Linha de comissões + dados bancários */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
            <tbody>
              <tr>
                <td style={{ background: '#f0f0f0', border: '1px solid #ccc', padding: '4px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13 }}>
                  <span>TOTAL DE COMISSÕES: <span style={{ fontSize: 15 }}>{brl(totalCv + totalDefCv + gtaNotaDef + totalDesp)}</span></span>
                  {orgDb && (
                    <span>
                      {' | '}BANCO: {orgDb.banco}{orgDb.agencia ? ` | AG: ${orgDb.agencia}  C/C: ${orgDb.conta}` : ''}{orgDb.pix ? ` | PIX: ${orgDb.pix}` : ''}
                    </span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      <div style={S.footer}>www.rematei.com.br</div>
    </div>
  )
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function MapaVendedorPreview({
  dados,
  showDbVendedor,
  showDbOrganizador = true,
  colunasAtivas = DEFAULT_COLUNAS_V1_VEND,
  orgId,
  leilaoId,
  vendedorId,
  isencaoDefesaAtiva = false,
  onReload,
}) {
  const iframeRef = useRef(null)
  const [pdfUrl,      setPdfUrl]      = useState(null)
  const [loadingPdf,  setLoadingPdf]  = useState(false)
  const [showPdfModal, setShowPdfModal] = useState(false)
  const [showAlterarComissao, setShowAlterarComissao] = useState(false)
  const [showDespesa, setShowDespesa] = useState(false)
  const [showIsencao, setShowIsencao] = useState(null)
  const [showAplicarIsencao, setShowAplicarIsencao] = useState(false)

  if (!dados) return null
  const { leilao, organizador, vendedores } = dados

  const handleImprimir = async () => {
    setLoadingPdf(true)
    try {
      const qs = new URLSearchParams()
      if (vendedorId) qs.set('vendedor_id', vendedorId)
      const resp = await api.get(
        `/organizadores/${orgId}/mapas/leilao/${leilaoId}/mapa-vendedor?${qs}`,
        { responseType: 'blob', timeout: 60000 }
      )
      const blob = new Blob([resp.data], { type: 'application/pdf' })
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(blob)
      openPdf(url, setPdfUrl, setShowPdfModal)
    } catch (err) {
      alert('Erro ao gerar PDF: ' + (err.response?.statusText || err.message))
    } finally {
      setLoadingPdf(false)
    }
  }

  const closePdfModal = () => {
    setShowPdfModal(false)
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) }
  }

  const printPdf = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus()
      iframeRef.current.contentWindow.print()
    }
  }

  const pdfFilename = (() => {
    const nomeVend = vendedores.length === 1
      ? vendedores[0].nome
      : `Todos (${vendedores.length})`
    return `Mapa do Vendedor - ${nomeVend}.pdf`
      .replace(/[/\\?%*:|"<>]/g, '-')
  })()

  return (
    <>
      {/* ── Barra de ação ── */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
        <p className="text-sm text-gray-600 flex-1 min-w-0 truncate">
          <span className="font-medium text-gray-800">{leilao.nome}</span>
          {' · '}
          {vendedores.length === 1 ? vendedores[0].nome : `${vendedores.length} vendedores`}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleImprimir}
            disabled={loadingPdf}
            className="flex items-center gap-1.5 px-4 py-2.5 sm:px-3 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-xs font-semibold rounded-lg transition-colors disabled:opacity-60"
          >
            <Printer className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">{loadingPdf ? 'Gerando...' : 'Imprimir'}</span>
            <span className="sm:hidden">{loadingPdf ? 'Gerando...' : 'PDF'}</span>
          </button>
          {vendedores.length === 1 && (
            <button onClick={async () => {
              try {
                const resp = await api.get(
                  `/organizadores/${orgId}/recibos/mapa-vendedor/${leilaoId}/${vendedores[0].id}/recibo-pagamento`,
                  { responseType: 'blob', timeout: 30000 }
                )
                const blob = new Blob([resp.data], { type: 'application/pdf' })
                if (pdfUrl) URL.revokeObjectURL(pdfUrl)
                openPdf(URL.createObjectURL(blob), setPdfUrl, setShowPdfModal)
              } catch (err) {
                import('react-hot-toast').then(m => m.default.error('Erro ao gerar recibo de pagamento'))
              }
            }}
              className="flex items-center gap-1 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium rounded-lg transition-colors"
              title="Recibo de pagamento do valor líquido ao vendedor">
              Recibo Pagamento
            </button>
          )}
          {vendedores.length === 1 && leilao.fluxo_comissao === 'separada' && (
            <button onClick={async () => {
              try {
                const resp = await api.get(
                  `/organizadores/${orgId}/recibos/mapa-vendedor/${leilaoId}/${vendedores[0].id}/recibo-comissao`,
                  { responseType: 'blob', timeout: 30000 }
                )
                const blob = new Blob([resp.data], { type: 'application/pdf' })
                if (pdfUrl) URL.revokeObjectURL(pdfUrl)
                openPdf(URL.createObjectURL(blob), setPdfUrl, setShowPdfModal)
              } catch (err) {
                import('react-hot-toast').then(m => m.default.error('Erro ao gerar recibo de comissão'))
              }
            }}
              className="flex items-center gap-1 px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-medium rounded-lg transition-colors"
              title="Recibo de recebimento de comissão + despesas">
              Recibo Comissão
            </button>
          )}
          {vendedores.length === 1 && (
            <button onClick={() => setShowAlterarComissao(true)}
              className="hidden sm:flex items-center gap-1 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition-colors"
              title="Alterar comissão de venda dos lotes">
              Alterar Com.
            </button>
          )}
          {vendedores.length === 1 && (
            <button onClick={() => setShowDespesa(true)}
              className="flex items-center gap-1 px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white text-xs font-medium rounded-lg transition-colors"
              title="Adicionar despesa nos lotes do vendedor">
              + Despesa
            </button>
          )}
          {/* Isentar/Desfazer Defesa */}
          {vendedores.length === 1 && (() => {
            const lotesDefesa = vendedores[0].lotes_defesa || vendedores[0].lotes_nao_movimentados?.filter(l => l.situacao === 'DEFESA') || []
            // defesa_elegivel null = produto "Isenção de Comissão de Defesa" desligado pro
            // organizador (comportamento de sempre: botão disponível pra qualquer defesa).
            // true/false = produto ligado: só mostra o botão quando o vendedor já se qualifica.
            const temComissaoDefesa = lotesDefesa.some(l =>
              l.cv_val > 0 && !l.defesa_isenta && (l.defesa_elegivel === null || l.defesa_elegivel === true)
            )
            const temIsento = lotesDefesa.some(l => l.defesa_isenta)
            const loteComPct = lotesDefesa.find(l => l.defesa_pct_defendido !== null && l.defesa_pct_defendido !== undefined)
            return (
              <>
                {isencaoDefesaAtiva && loteComPct && (
                  <span className="flex items-center px-2 py-2 text-sm font-semibold text-gray-800" title="% defendido (em valor) desse vendedor nesse leilão">
                    defendeu {loteComPct.defesa_pct_defendido.toFixed(2)}%
                  </span>
                )}
                {temComissaoDefesa && (
                  <button onClick={() => setShowIsencao('isentar')}
                    className="flex items-center gap-1 px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg transition-colors"
                    title="Zerar comissão de defesa">
                    Isentar Defesa
                  </button>
                )}
                {temIsento && (
                  <button onClick={() => setShowIsencao('desfazer')}
                    className="flex items-center gap-1 px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white text-xs font-medium rounded-lg transition-colors"
                    title="Restaurar comissão de defesa original">
                    Desfazer Isenção
                  </button>
                )}
              </>
            )
          })()}
          {isencaoDefesaAtiva && (
            <button onClick={() => setShowAplicarIsencao(true)}
              className="flex items-center gap-1 px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg transition-colors"
              title="Sugerir e aplicar isenção de defesa pros vendedores elegíveis do leilão inteiro (não só o selecionado aqui)">
              Aplicar Isenção (leilão)
            </button>
          )}
        </div>
      </div>

      {/* ── Aviso mobile (oculto em sm+) ── */}
      <div className="sm:hidden bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
        <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p>O preview é otimizado para desktop.</p>
        <p className="mt-1">Use os botões acima para gerar o PDF.</p>
      </div>

      {/* ── Preview JSX (oculto no mobile) ── */}
      <div className="hidden sm:block" style={{ background: '#f3f4f6', padding: '24px 0' }}>
        {vendedores.map((vend, i) => (
          <div
            key={vend.id || i}
            style={{
              maxWidth: 900,
              margin: '0 auto 24px',
              background: '#fff',
              boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            <MapaPage
              vend={vend}
              leilao={leilao}
              organizador={organizador}
              showDbVendedor={showDbVendedor}
              showDbOrganizador={showDbOrganizador}
              colunasAtivas={colunasAtivas}
            />
          </div>
        ))}
      </div>

      {/* ── Modal PDF ── */}
      {showPdfModal && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white w-full max-w-5xl flex flex-col rounded-t-2xl sm:rounded-xl shadow-2xl" style={{ height: '92dvh' }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl sm:rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800 text-sm truncate flex-1 min-w-0">Mapa do Vendedor — {leilao.nome}</span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => downloadPdf(pdfUrl, pdfFilename)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Salvar PDF
                </button>
                <button
                  onClick={printPdf}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <Printer className="w-4 h-4" />
                  Imprimir
                </button>
                <button
                  onClick={closePdfModal}
                  className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                >
                  Fechar
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe
                ref={iframeRef}
                src={pdfUrl}
                title={pdfFilename}
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal Alterar Comissão */}
      {showAlterarComissao && vendedores.length === 1 && (
        <AlterarComissaoModal
          tipo="venda"
          lotes={(vendedores[0].lotes_venda || []).filter(l => l.id)}
          organizadorId={orgId}
          onClose={() => setShowAlterarComissao(false)}
          onSaved={() => { if (onReload) onReload() }}
        />
      )}

      {/* Modal Adicionar Despesa */}
      {showDespesa && vendedores.length === 1 && (
        <AdicionarDespesaModal
          lotes={[vendedores[0].lotes_venda, vendedores[0].lotes_defesa, vendedores[0].lotes_nao_movimentados]
            .map(arr => (arr || []).filter(l => l.id))
            .find(arr => arr.length > 0) || []}
          organizadorId={orgId}
          onClose={() => setShowDespesa(false)}
          onSaved={() => { if (onReload) onReload() }}
        />
      )}

      {/* Modal Isentar/Desfazer Defesa */}
      {showIsencao && vendedores.length === 1 && (
        <IsentarDefesaModal
          modo={showIsencao}
          lotesDefesa={vendedores[0].lotes_defesa || vendedores[0].lotes_nao_movimentados?.filter(l => l.situacao === 'DEFESA') || []}
          organizadorId={orgId}
          onClose={() => setShowIsencao(null)}
          onSaved={() => { setShowIsencao(null); if (onReload) onReload() }}
        />
      )}

      {/* Modal Aplicar Isenção (sugestão em lote pro leilão inteiro) */}
      {showAplicarIsencao && (
        <AplicarIsencaoModal
          organizadorId={orgId}
          leilaoId={leilaoId}
          onClose={() => setShowAplicarIsencao(false)}
          onSaved={() => { setShowAplicarIsencao(false); if (onReload) onReload() }}
        />
      )}
    </>
  )
}
